import {
  Alert,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { enqueueSnackbar } from 'notistack';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthState } from 'state/useAuthState';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';

interface OrganizationQuoteData {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  additionalEmails?: string;
  postalCode: string;
  poNumber?: string;
  regularSeats: number;
  proSeats: number;
}

export const OrganizationQuoteInfoForm = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { sendOrganizationQuote } = useOrganizationPurchase({});
  const { regularSeats, proSeats, ...orgState } = useOrganizationState();
  const { user } = useAuthState();

  const { control, handleSubmit, formState } = useForm<OrganizationQuoteData>({
    resolver: yupResolver(
      getOrgCommonSignupFormsValidationSchema({
        t,
        requireExistingUserToLogin: true,
        emailValidationCache: new Map(),
        fields: [
          'companyName',
          'firstName',
          'lastName',
          'email',
          'additionalEmails',
          'address',
          'postalCode',
          'regularSeats',
          'proSeats',
          'poNumber',
        ],
      }),
    ),
    defaultValues: {
      companyName: orgState.companyName || '',
      firstName: orgState.firstName || '',
      lastName: orgState.lastName || '',
      email: orgState.email || user?.email || '',
      address: '',
      additionalEmails: '',
      postalCode: '',
      poNumber: '',
      regularSeats: regularSeats || 0,
      proSeats: proSeats || 0,
    },
    mode: 'onBlur',
  });

  const { errors } = formState;

  const handleFormSubmit = async (
    values: OrganizationQuoteData,
  ): Promise<void> => {
    try {
      const data = {
        ...values,
        regularSeats,
        proSeats,
      };

      // remove empty poNumber if it exists
      if (!data.poNumber) {
        data.poNumber = undefined;
      }

      await sendOrganizationQuote(data);

      enqueueSnackbar(t('organizationPurchase.quoteSuccess'), {
        variant: 'success',
      });
      history.push('/');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorKey = error?.response?.data?.[0];
      if (errorKey === 'organization-already-exists') {
        enqueueSnackbar(t('organizationPurchase.quoteOrgExists'), {
          variant: 'error',
          persist: true,
        });
      } else {
        logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      }
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Stack spacing={2}>
        <Grid>
          <Typography variant="h3">
            <b>{t('organizationPurchase.quoteInfo')}</b>
          </Typography>
        </Grid>

        {errors.email && errors.email.type === 'registered' && (
          <Grid>
            <Alert icon={false} severity="error" sx={{ color: 'error.main' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                  marginBottom: '16px',
                }}
              >
                {errors.email.message}
              </Typography>

              <MuiLink
                to="/login"
                component={Link}
                color="inherit"
                underline="always"
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {t('membershipCheckout.login')}
              </MuiLink>
            </Alert>
          </Grid>
        )}

        <Grid>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.companyName.label')}
                error={Boolean(errors.companyName)}
                helperText={errors.companyName?.message}
                required
                {...field}
              />
            )}
          />
        </Grid>

        <Grid container spacing={2} direction="row">
          <Grid size={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.firstName.label')}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.lastName.label')}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.email.label')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                disabled={Boolean(user?.email)}
                required
                {...field}
              />
            )}
          />
        </Grid>

        <Grid>
          <Controller
            name="additionalEmails"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.additionalEmails.label')}
                helperText={
                  errors.additionalEmails?.message ||
                  t('organizationPurchase.additionalEmails.helperText')
                }
                error={Boolean(errors.additionalEmails)}
                {...field}
              />
            )}
          />
        </Grid>

        <Grid>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.address.label')}
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
                required
                {...field}
              />
            )}
          />
        </Grid>

        <Grid container spacing={2} direction="row">
          <Grid size={6}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.zipcode.label')}
                  error={Boolean(errors.postalCode)}
                  helperText={errors.postalCode?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid>
          <Controller
            name="poNumber"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.poNumber.label')}
                error={Boolean(errors.poNumber)}
                helperText={errors.poNumber?.message}
                {...field}
              />
            )}
          />
        </Grid>

        <Grid>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid>

        <OrganizationPurchaseSummary
          showPerPlanType
          regularSeats={regularSeats}
          proSeats={proSeats}
        />
      </Stack>

      <Stack sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        <Button
          size="large"
          fullWidth
          type="submit"
          disabled={formState.isSubmitting}
        >
          {t('organizationPurchase.sendAndEmailQuote')}
        </Button>
      </Stack>
    </Stack>
  );
};
